import React from "react";

export type TabsVariant = "default" | "segmented";

interface TabProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  variant: TabsVariant;
}

const Tab: React.FC<TabProps> = ({ label, isActive, onClick, variant }) => {
  if (variant === "segmented") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`shrink-0 whitespace-nowrap rounded-lg border px-5 py-2.5 text-center text-sm transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2 ${isActive
          ? "border-gray-200 bg-white font-semibold shadow-sm"
          : "border-transparent font-medium opacity-60 hover:opacity-100"
          }`}
      >
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm transition-colors duration-200 focus:outline-none ${isActive
        ? "font-semibold text-gray-900"
        : "font-medium text-gray-500 hover:text-gray-700"
        }`}
    >
      {label}
    </button>
  );
};

interface TabsProps {
  tabs: string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  variant?: TabsVariant;
}

const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onTabChange, variant = "default" }) => {
  if (variant === "segmented") {
    return (
      <div
        className="inline-flex w-fit max-w-full rounded-lg bg-impact-plus-page p-1.5"
        role="tablist"
        aria-label="Segmented control"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab}
            label={tab}
            isActive={activeTab === tab}
            onClick={() => onTabChange(tab)}
            variant="segmented"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex overflow-x-auto bg-impact-plus-page rounded-t-lg px-2 py-1" role="tablist" aria-label="Tabs">
      <div className="flex min-w-max items-end">
        {tabs.map((tab) => (
          <Tab
            key={tab}
            label={tab}
            isActive={activeTab === tab}
            onClick={() => onTabChange(tab)}
            variant="default"
          />
        ))}
      </div>
    </div>
  );
};

export default Tabs;
